/**
 * AX-Agent Add-on 임베드 설정 (docs/designs/mes-agent-addon-integration.md §3.3·§3.4).
 *
 * - `VITE_AGENT_EMBED_URL` — 임베드 페이지 URL. 레거시 `VITE_MES_AGENT_PANEL_URL` 폴백 지원.
 * - `VITE_AGENT_TENANT` — ax-agent 테넌트 슬러그 강제 지정. 미설정 시 로그인 사용자의
 *   tenantCode 를 사용하므로 대부분의 테넌트는 env 0줄로 동작한다.
 */

const env = import.meta.env as Record<string, string | undefined>;

export const AGENT_EMBED_URL =
  env.VITE_AGENT_EMBED_URL?.trim() ||
  env.VITE_MES_AGENT_PANEL_URL?.trim() ||
  'https://agent-embed.axsolutions.kr/';

/** ax-agent 테넌트 슬러그 오버라이드 (비우면 authStore.user.tenantCode). */
export const AGENT_TENANT_OVERRIDE = env.VITE_AGENT_TENANT?.trim() || '';

const parsedWidth = Number.parseInt(
  (env.VITE_AGENT_PANEL_WIDTH ?? env.VITE_MES_AGENT_PANEL_WIDTH ?? '').trim(),
  10,
);

export const AGENT_PANEL_MIN_WIDTH = 480;
export const AGENT_PANEL_MAX_WIDTH = 1200;

/** 우측 Agent 패널 기본 너비(px). */
export const AGENT_PANEL_WIDTH =
  Number.isFinite(parsedWidth) && parsedWidth >= AGENT_PANEL_MIN_WIDTH ? parsedWidth : 640;

/** localStorage 키 — 사용자가 드래그로 조절한 너비 유지 (레거시 mes 키 폴백 판독). */
export const AGENT_PANEL_WIDTH_STORAGE_KEY = 'agent-panel-width';
const LEGACY_WIDTH_STORAGE_KEY = 'mes-agent-panel-width';

/** node(vitest)/SSR 에서는 window 가 없다 — import-time 크래시 방지용 기본 뷰포트. */
function viewportWidthOrDefault(): number {
  return typeof window === 'undefined' ? 1920 : window.innerWidth;
}

export function resolveAgentPanelMaxWidth(viewportWidth = viewportWidthOrDefault()): number {
  return Math.min(
    AGENT_PANEL_MAX_WIDTH,
    Math.max(AGENT_PANEL_MIN_WIDTH, Math.floor(viewportWidth * 0.72)),
  );
}

export function clampAgentPanelWidth(
  width: number,
  viewportWidth = viewportWidthOrDefault(),
): number {
  const max = resolveAgentPanelMaxWidth(viewportWidth);
  return Math.min(max, Math.max(AGENT_PANEL_MIN_WIDTH, Math.round(width)));
}

export function readStoredAgentPanelWidth(): number {
  if (typeof localStorage === 'undefined') {
    return clampAgentPanelWidth(AGENT_PANEL_WIDTH);
  }
  const raw =
    localStorage.getItem(AGENT_PANEL_WIDTH_STORAGE_KEY) ??
    localStorage.getItem(LEGACY_WIDTH_STORAGE_KEY);
  const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
  if (!Number.isFinite(parsed)) {
    return clampAgentPanelWidth(AGENT_PANEL_WIDTH);
  }
  return clampAgentPanelWidth(parsed);
}

export function storeAgentPanelWidth(width: number): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(AGENT_PANEL_WIDTH_STORAGE_KEY, String(width));
}
